import { FormEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EditOutlined, RotateRightOutlined } from '@mui/icons-material';
import { Avatar, Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { usePortal_GetPortalByIdQuery, usePortal_UpdatePortalLogoByIdMutation } from 'queries';

import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  onExitClick: () => void;
};

const useStyles = makeStyles((theme) => ({
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 140,
    width: 140,
    marginRight: 10,
    borderRadius: 9999,
    backgroundColor: '#1C2541',
  },
  logo: {
    height: '140px',
    width: '140px',
    [theme.breakpoints.down('sm')]: {
      height: '80px',
      width: '80px',
    },
    background: 'white',
    marginBottom: '5px',
  },
  buttonText: {
    marginLeft: '11px',
  },
  grid: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    marginTop: 20,
  },
  formActionbuttons: {
    padding: '20px',
    paddingRight: '0',
    textAlign: 'right',
  },
  saveButton: {
    color: 'black',
    backgroundColor: '#F5DE76',
  },
  cancelButton: {
    color: '#ffffff',
    backgroundColor: '#8DA8B2',
    marginLeft: '15px',
  },
}));

const ChangePortalLogoForm = ({ onExitClick }: Props) => {
  const { portalId } = useParams<Record<string, string>>();
  const classes = useStyles();

  const { data: portalData, refetch: refetchPortal } = usePortal_GetPortalByIdQuery({
    skip: !portalId,
    variables: { id: portalId },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [portalLogoFile, setPortalLogoFile] = useState<File>();
  const [portalLogoUrl, setPortalLogoUrl] = useState<string>(portalData?.data?.logoUrl || '');

  const [updatePortalLogo, { loading: isUpdatingPortalLogo }] =
    usePortal_UpdatePortalLogoByIdMutation({
      onCompleted: () => {
        showToast({
          message: `Portal logo updated successfully`,
          severity: NotificationSeverity.SUCCESS,
        });
        refetchPortal();
        setPortalLogoFile(undefined);
      },
      onError: (e) =>
        showAlert({
          message: `Error updating portal logo: ${e.message}`,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const submitForm = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    await updatePortalLogo({
      variables: {
        input: {
          logo: portalLogoFile,
          portalId,
        },
      },
    });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      showAlert({
        message: 'No file selected.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    const fileSize = file.size;
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (fileSize > maxSize) {
      showAlert({
        message: 'File size exceeds 10MB limit',
        severity: NotificationSeverity.ERROR,
      });

      return;
    }

    const fileUrl = URL.createObjectURL(file);
    setPortalLogoUrl(fileUrl);
    setPortalLogoFile(e.target.files?.[0]);
  };

  return (
    <>
      <Typography variant="h5">Portal Logo</Typography>
      <form id="change-portal-logo-form" onSubmit={submitForm}>
        <Grid spacing={3} xs={12} className={classes.grid}>
          <div className={classes.logoContainer}>
            <Avatar
              alt="Portal logo"
              src={portalLogoUrl ? portalLogoUrl : '/trustate-logo-white.svg'}
              className={classes.logo}
            />
            <input
              id="portal-logo"
              type="file"
              accept="image/png, image/jpeg, image/svg+xml"
              onChange={handleLogoChange}
              hidden
              disabled={isUpdatingPortalLogo}
            />
          </div>
        </Grid>
        <Grid spacing={3} xs={12}>
          <label htmlFor="portal-logo">
            <Button component="span" disabled={isUpdatingPortalLogo}>
              <EditOutlined name="edit" />
              <Typography className={classes.buttonText} variant="caption">
                Edit Portal Logo
              </Typography>
            </Button>
          </label>
        </Grid>
        <Grid xs={12} className={classes.grid}>
          <Button
            type="submit"
            className={classes.saveButton}
            disabled={!portalLogoFile || isUpdatingPortalLogo}
          >
            Save Changes
            {isUpdatingPortalLogo && (
              <RotateRightOutlined
                sx={{
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '1000%': {
                      transform: 'rotate(360deg)',
                    },
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                  },
                }}
              />
            )}
          </Button>
          <Button
            className={classes.cancelButton}
            onClick={onExitClick}
            disabled={isUpdatingPortalLogo}
          >
            Cancel
          </Button>
        </Grid>
      </form>
    </>
  );
};

export default ChangePortalLogoForm;
